import { useState } from "react";
import Header from "../../components/feature/Header";
import Footer from "../../components/feature/Footer";
import ScrollToTop from "../../components/feature/ScrollToTop";
import SEOHead from "../../components/base/SEOHead";
import { lineups } from "../../mocks/lineup";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://xn--9z2b23f95curk71m6qd.com";

const lineupJsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "스포츠중계 라인업",
  description: "오늘 경기의 선발 라인업과 대기 명단을 확인하세요.",
  numberOfItems: lineups.length,
  itemListElement: lineups.map((lineup, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "SportsEvent",
      name: lineup.match,
      homeTeam: { "@type": "SportsTeam", name: lineup.homeTeam },
      awayTeam: { "@type": "SportsTeam", name: lineup.awayTeam },
    },
  })),
};

export default function Lineup() {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-dark-bg">
      <SEOHead
        title="스포츠중계 라인업 | 선발 명단 및 포메이션 - 할매티비"
        description="오늘 경기의 스포츠중계 라인업을 확인하세요. 선발 명단, 포메이션, 교체 명단까지 경기 시작 1시간 전부터 실시간 업데이트되는 정보를 가장 빠르게 제공합니다."
        keywords="스포츠중계라인업,선발라인업,포메이션,축구라인업,선수명단,교체명단"
        canonicalPath="/lineup"
        jsonLd={lineupJsonLd}
      />
      <Header />
      <main className="pt-24 md:pt-32 pb-16">
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
          {/* Page Title */}
          <div className="mb-8">
            <h1 className="text-white font-bold text-2xl md:text-3xl mb-2">
              스포츠중계 라인업
            </h1>
            <p className="text-dark-text-muted text-base">
              오늘 경기의 선발 라인업과 대기 명단을 확인하세요.
            </p>
          </div>

          {/* Lineup Cards */}
          <div className="space-y-4 mb-12">
            {lineups.map((lineup) => (
              <div
                key={lineup.id}
                className="bg-dark-surface border border-dark-border rounded-xl overflow-hidden hover:border-brand-orange transition-all"
              >
                {/* Header */}
                <button
                  onClick={() => setExpandedId(expandedId === lineup.id ? null : lineup.id)}
                  className="w-full p-4 md:p-5 flex items-center justify-between cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-dark-text-muted text-xs bg-dark-bg px-3 py-1 rounded-full">
                      {lineup.league}
                    </span>
                    <span className="text-white font-bold text-sm md:text-base">
                      {lineup.match}
                    </span>
                    <span className="text-brand-orange text-sm font-bold">{lineup.time}</span>
                  </div>
                  <div className="w-8 h-8 flex items-center justify-center">
                    {expandedId === lineup.id ? (
                      <i className="ri-arrow-up-s-line text-dark-text-muted text-xl"></i>
                    ) : (
                      <i className="ri-arrow-down-s-line text-dark-text-muted text-xl"></i>
                    )}
                  </div>
                </button>

                {/* Expanded Content */}
                {expandedId === lineup.id && (
                  <div className="px-4 md:px-5 pb-5 border-t border-dark-border">
                    {/* Formation */}
                    <div className="flex items-center justify-center gap-8 py-4">
                      <div className="text-center">
                        <span className="text-dark-text-muted text-xs block mb-1">{lineup.homeTeam}</span>
                        <span className="text-brand-orange font-bold text-lg">{lineup.homeFormation}</span>
                      </div>
                      <span className="text-dark-text-muted text-sm">VS</span>
                      <div className="text-center">
                        <span className="text-dark-text-muted text-xs block mb-1">{lineup.awayTeam}</span>
                        <span className="text-brand-orange font-bold text-lg">{lineup.awayFormation}</span>
                      </div>
                    </div>

                    {/* Lineups */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Home Lineup */}
                      <div>
                        <h3 className="text-white font-bold text-sm mb-3">{lineup.homeTeam} 선발</h3>
                        <div className="space-y-1">
                          {lineup.homeLineup.map((player, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <span className="text-brand-orange font-bold w-6">{i + 1}</span>
                              <span className="text-dark-text-muted">{player}</span>
                            </div>
                          ))}
                        </div>
                        <h4 className="text-dark-text-muted text-xs mt-4 mb-2">교체 명단</h4>
                        <div className="flex flex-wrap gap-2">
                          {lineup.homeSubstitutes.slice(0, 8).map((player, i) => (
                            <span key={i} className="text-dark-text-muted text-xs bg-dark-bg px-2 py-1 rounded">
                              {player}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Away Lineup */}
                      <div>
                        <h3 className="text-white font-bold text-sm mb-3">{lineup.awayTeam} 선발</h3>
                        <div className="space-y-1">
                          {lineup.awayLineup.map((player, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <span className="text-brand-orange font-bold w-6">{i + 1}</span>
                              <span className="text-dark-text-muted">{player}</span>
                            </div>
                          ))}
                        </div>
                        <h4 className="text-dark-text-muted text-xs mt-4 mb-2">교체 명단</h4>
                        <div className="flex flex-wrap gap-2">
                          {lineup.awaySubstitutes.slice(0, 8).map((player, i) => (
                            <span key={i} className="text-dark-text-muted text-xs bg-dark-bg px-2 py-1 rounded">
                              {player}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* SEO Content */}
          <div className="border-t border-dark-border pt-8">
            <h2 className="text-white font-bold text-xl mb-4">스포츠중계 라인업 확인의 중요성</h2>
            <p className="text-dark-text-muted text-sm leading-relaxed mb-4">
              <strong className="text-white">스포츠중계 라인업</strong>은 경기 결과를 예측하는 데 가장 중요한 정보입니다. 선발 선수의 구성, 포메이션, 교체 명단까지 확인해야 정확한 경기 분석이 가능합니다.
            </p>
            <p className="text-dark-text-muted text-sm leading-relaxed mb-4">
              <strong className="text-white">할매티비</strong>의 <strong className="text-white">스포츠중계 라인업</strong> 정보는 경기 시작 1시간 전부터 실시간으로 업데이트됩니다. 주요 리그의 선발 명단을 가장 빠르게 확인하고, 전술 변화와 선수 컨디션을 파악하세요.
            </p>
            <p className="text-dark-text-muted text-sm leading-relaxed">
              <strong className="text-white">스포츠중계</strong>를 시청하기 전에 반드시 라인업을 확인하세요. 핵심 선수의 결장 여부, 포메이션 변경, 신인 선수 출전 등 경기의 모든 변수를 라인업에서 확인할 수 있습니다.
            </p>
          </div>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}